import React, { useContext } from 'react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import {
  BookmarkBorderRounded,
  BookmarkRounded,
  IosShareRounded,
  PlaylistPlayRounded,
  MoreHorizRounded,
} from '@mui/icons-material';
import { IMenuLink } from '@/infrastructure/interfaces/i-link';
import { useTranslation } from 'next-i18next';
import { NavigatorEvents } from '@/utils/helpers/navigator-events';
import { AudioSurah } from '@/infrastructure/class/audio-surah';
import AddToPlaylistMenuItem from './playlist-btn';
import { Playlist } from '@/infrastructure/class/playlist';
import { useAuth } from '@/infrastructure/context/auth-context';
import { usePlayer } from '@/infrastructure/context/player-context';
import { useAudioBookmarks } from '@/infrastructure/context/audio-bookmarks-context';
import { ReadSurahSlugsContext } from '@/infrastructure/context/read-surah-slugs-context';
import { getAudioSurahPath } from '@/infrastructure/functions/get-audio-surah-path';

interface IAudioSurahsListItemMenuBtnProps {
  data: AudioSurah;
  playlist?: Playlist;
  reciterSlug?: string;
  className?: string;
}

const AudioSurahsListItemMenuBtn: React.FC<IAudioSurahsListItemMenuBtnProps> = (
  props
) => {
  const { data, playlist, reciterSlug, className = '' } = props;
  const { playNext } = usePlayer();
  const { getSurahSlugByNumber } = useContext(ReadSurahSlugsContext);

  const { audioBookmarks, changeAudioBookmark } = useAudioBookmarks();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { authUser, checkAuth, showMessage } = useAuth();

  const bookmark = audioBookmarks.isBookmark(data);

  const { t: tButtons } = useTranslation('buttons');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const onShare = async () => {
    const path = getAudioSurahPath({
      reciterSlug: reciterSlug ?? data.reciter?.slug,
      surahNumber: data.number,
      getSurahSlugByNumber,
    });
    await NavigatorEvents.copyString(`${window.location.origin}${path}`);
    handleClose();
    showMessage(tButtons('linkCopied'));
  };

  const onBookmark = async () => {
    handleClose();
    await checkAuth();
    if (authUser) {
      await changeAudioBookmark({ user: authUser, audioSurah: data });
      const message = bookmark
        ? tButtons('removedFromBookmarks')
        : tButtons('addedToBookmarks');
      showMessage(message);
    }
  };
  const onPlayNext = () => {
    playNext(data);
    handleClose();
  };

  const menuItems: IMenuLink[] = [
    {
      title: tButtons('addToBookmark'),
      icon: BookmarkBorderRounded,
      action: onBookmark,
      visible: !bookmark,
    },
    {
      title: tButtons('removeFromBookmark'),
      icon: BookmarkRounded,
      action: onBookmark,
      visible: !!bookmark,
    },
    {
      title: tButtons('playNext'),
      icon: PlaylistPlayRounded,
      action: onPlayNext,
      visible: true,
    },
    {
      title: tButtons('share'),
      icon: IosShareRounded,
      action: onShare,
      visible: true,
    },
  ];

  return (
    <div className={className} onClick={(e) => e.stopPropagation()}>
      <IconButton
        aria-label="menu button"
        color="error"
        onClick={handleClick}
        size="small"
      >
        <MoreHorizRounded />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <AddToPlaylistMenuItem
          audioSurah={data}
          currentPlaylist={playlist}
          onClose={handleClose}
        />
        {menuItems.map(
          (menuItem) =>
            menuItem.visible && (
              <MenuItem key={menuItem.title} onClick={menuItem.action}>
                {menuItem.icon && (
                  <i>
                    <menuItem.icon />
                  </i>
                )}
                <span>{menuItem.title}</span>
              </MenuItem>
            )
        )}
      </Menu>
    </div>
  );
};

export default AudioSurahsListItemMenuBtn;
